import React, { useState, useEffect, useContext } from 'react';

import DialogContent from '@material-ui/core/DialogContent';
import { Dialog } from '@material-ui/core';
import CloseIcon from '../../assets/images/icons/close-black.svg?react';
import styles from './requestModal.module.css';
import Form from '../Form';
import PreCancelDialog from './PreCancelDialog';
import { successToast, errorToast } from '../toast';
import { requestQualificationUpdate, getUserData } from '../../services/api';
import AppContext from '../../context/AppContext';
import { setResumeItems, importData } from '../../utils';
import { QUALIFICATION_TYPES } from '../../constants/qualificationTypes';

const RequestUpdateModal = ({ item, open, setOpen, type = 'Certification' }) => {
  const [localItem, setLocalItem] = useState({});
  const [errors, setErrors] = useState({});
  const [isOpenPreCancel, setIsOpenPreCancel] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const context = useContext(AppContext);
  const { state, dispatch } = context;
  const { ownUserInfo, selectedCvId, ownCvId } = state;
  const userId = ownUserInfo?.userId;

  useEffect(() => {
    if (open && item) {
      const qualificationType = item.type || type || QUALIFICATION_TYPES.CERTIFICATE;
      setLocalItem({ ...item, type: qualificationType });
      setErrors({});
    }
  }, [open, item, type]);

  const handleLocalChange = (field, value) => {
    if (value === undefined) {
      return;
    }

    setLocalItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const refreshUserData = async () => {
    if (!userId) return;
    try {
      const res = await getUserData(userId);
      if (res?.length) {
        setResumeItems(dispatch, res);
        const currentCvItem =
          res.find((r) => r.id === selectedCvId) ??
          res.find((r) => r.id === ownCvId) ??
          res.find((r) => r.isMain) ??
          res[0];
        if (currentCvItem) {
          importData(dispatch, { data: { ...currentCvItem } });
        }
      }
    } catch {
      errorToast('Error refreshing user data');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const { personId } = ownUserInfo;

      if (!personId) {
        errorToast('User ID is missing. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const skillsSource = localItem.skills || localItem.tags || [];
      const apiData = {
        id: localItem.id,
        personId,
        name: localItem.name || localItem.key || null,
        type: localItem.type || QUALIFICATION_TYPES.CERTIFICATE,
        credentialLink: localItem.credentialLink || localItem.link || null,
        issuedBy: localItem.issuedBy || localItem.issued || null,
        stateAccredited: localItem.stateAccredited ?? localItem.isStateAccredited ?? false,
        accreditationCountry: localItem.accreditationCountry || localItem.country || null,
        issueDate:
          localItem.issueDate || localItem.start
            ? formatDate(localItem.issueDate || localItem.start)
            : null,
        expirationDate:
          localItem.expirationDate || localItem.end
            ? formatDate(localItem.expirationDate || localItem.end)
            : null,
        skills: skillsSource.map((tag) => {
          const tagObj = typeof tag === 'object' ? tag : { tag: { id: null, tag } };
          const tagId = tagObj._id ?? tagObj.id ?? tagObj.tag?.id ?? null; // eslint-disable-line no-underscore-dangle
          const tagValue = tagObj.tag?.tag ?? tagObj.value ?? tagObj.key ?? tagObj.tag ?? tag;
          return {
            tag: {
              id: tagId,
              tag: tagValue,
            },
            id: null,
            personId: null,
            starRating: 0,
          };
        }),
        status: 'PENDING',
        file: null,
        fileName: null,
      };

      await requestQualificationUpdate(personId, apiData);
      await refreshUserData();

      setOpen(false);
      successToast('Request to update education has been submitted.');
    } catch (error) {
      errorToast(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpenPreCancel(false);
    setOpen(false);
  };

  return (
    <>
      <PreCancelDialog
        isOpen={isOpenPreCancel}
        setIsOpen={setIsOpenPreCancel}
        onDismiss={() => setIsOpenPreCancel(false)}
        handleClose={handleClose}
      />
      <Dialog
        style={{ overflow: 'visible', padding: 18 }}
        open={open}
        classes={{
          root: styles.popupRoot,
          paper: styles.popupContainer,
        }}
        BackdropProps={{
          classes: {
            root: styles.backDrop,
          },
        }}
        keepMounted
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ fontSize: 26, fontWeight: 600 }}>Request an update</p>
            <p style={{ fontSize: 12, fontWeight: 400, color: '#718096' }}>
              Submit education for HR review. You’ll be notified once your request is processed.
              Please make sure all fields are correct.
            </p>
          </div>

          <CloseIcon
            style={{
              position: 'absolute',
              top: 10,
              right: 0,
              cursor: 'pointer',
              width: 15,
              height: 15,
            }}
            onClick={() => setOpen(false)}
            fill="#718096"
          />
        </div>

        <DialogContent style={{ overflow: 'visible', padding: 0 }}>
          <Form
            item={localItem}
            onChange={handleLocalChange}
            errors={errors}
            setErrors={setErrors}
            isDisabled={false}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              className="btn_cancel rounded-md"
              type="button"
              onClick={() => setIsOpenPreCancel(true)}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="btn_my_cv rounded-md px-3"
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit for review'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequestUpdateModal;
